import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { CalendarClock, Camera, ChevronRight, MapPinOff, MessageSquareWarning } from 'lucide-react';
import type { WriteOffListRow } from '@eombor/contracts';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { WriteOffStatusBadge } from './WriteOffStatusBadge.js';
import { WriteOffStockLine } from './WriteOffStockLine.js';
import {
  STATUS_STRIPE,
  dayOf,
  enterFrom,
  enterTransition,
  qtyText,
  unitOf,
  type WriteOffRowsViewProps,
} from './write-off-row.js';

/**
 * TOR EKRAN ro'yxati — telefonda bitta, planshetda ikki ustun.
 *
 * Jadval bilan AYNAN bir xil props oladi: sahifa qaysi biri chizilishini
 * kenglikka qarab tanlaydi, ma'lumot va handler esa bitta joyda qoladi.
 */
export const WriteOffCardList = ({ rows, onOpen, reduceMotion }: WriteOffRowsViewProps) => (
  <ul className="grid gap-3 md:grid-cols-2">
    {rows.map((row, index) => (
      <motion.li
        key={row.id}
        initial={enterFrom(reduceMotion)}
        animate={{ opacity: 1, y: 0 }}
        transition={enterTransition(index, reduceMotion)}
        className="min-w-0"
      >
        <WriteOffCard row={row} onOpen={() => onOpen(row.id)} />
      </motion.li>
    ))}
  </ul>
);

/**
 * Bitta akt — KARTOCHKA.
 *
 * `<article>` ataylab: har akt mustaqil hujjat va ekran o'quvchi ular
 * orasida bo'lim sifatida yura oladi. Butun kartochka sichqoncha bilan
 * bosiladi (qulaylik), klaviatura va ekran o'quvchi uchun esa pastda
 * haqiqiy «Batafsil» tugmasi bor — ichma-ich interaktiv element yo'q.
 */
export const WriteOffCard = ({ row, onOpen }: { row: WriteOffListRow; onOpen: () => void }) => {
  const when = dayOf(row.submittedAt);

  return (
    <article
      onClick={onOpen}
      className={cn(
        'group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl',
        'border border-border bg-surface shadow-[var(--shadow-1)]',
        'transition-[box-shadow,border-color,transform] duration-[var(--motion-base)]',
        'ease-[var(--ease-standard)] hover:border-border-strong hover:shadow-[var(--shadow-2)]',
        'motion-safe:hover:-translate-y-px',
      )}
    >
      {/* Holat chizig'i — ro'yxatni ko'z bilan tez saralash uchun. Ma'no
          badge'da matn bilan ham bor, bu faqat qo'shimcha ishora. */}
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', STATUS_STRIPE[row.status])} />

      <div className="flex flex-1 flex-col gap-3 p-4 pl-5">
        <header className="flex items-start justify-between gap-3">
          <span className="tabular min-w-0 truncate pt-0.5 font-mono text-sm font-semibold tracking-tight text-text">
            {row.docNo}
          </span>
          <WriteOffStatusBadge status={row.status} className="shrink-0" />
        </header>

        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="text-base leading-6 font-semibold text-pretty text-text">
            {row.nomenclatureName}
          </h3>
          <p className="flex items-baseline gap-1.5">
            <span className="tabular text-2xl leading-8 font-semibold tracking-tight text-text">
              {qtyText(row.qty)}
            </span>
            <span className="text-sm text-text-muted">{unitOf(row)}</span>
          </p>
          {/* «2 donadan 1 tasi» — ochmasdan turib ko'rinadi. */}
          <WriteOffStockLine act={row} className="text-xs text-pretty text-text-muted" />
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-xl bg-surface-2/60 p-3 text-sm">
          <Fact label={t('writeOff.subdivision')} value={row.subdivisionName || '—'} />
          <Fact label={t('writeOff.author')} value={row.createdByName ?? '—'} />
          <Fact label={t('writeOff.batch')} value={row.batchNo || '—'} mono />
          <Fact
            label={t('writeOff.photos')}
            value={
              <span className="inline-flex items-center gap-1">
                <Camera size={14} aria-hidden className="text-text-muted" />
                <span className="tabular">{row.photoCount}</span>
              </span>
            }
          />
        </dl>

        {/* «Lokatsiyasiz» — buxgalter buni ro'yxatda ko'rishi kerak. `<dl>`
            dan tashqarida: bu atama–qiymat juftligi emas, BELGI. */}
        {row.locationLat === null ? (
          <p className="flex items-center gap-1.5 text-xs font-medium text-warn">
            <MapPinOff size={14} aria-hidden />
            {t('writeOff.noLocation')}
          </p>
        ) : null}

        {/* Oldingi rad etish sababi — qayta yuborilgan akt navbatda
            boshqalar bilan aralashib ketmasin. */}
        {row.lastRejectReason === null ? null : (
          <p className="flex items-start gap-2 rounded-xl bg-danger-soft px-3 py-2 text-xs leading-5 text-danger">
            <MessageSquareWarning size={14} aria-hidden className="mt-0.5 shrink-0" />
            <span className="min-w-0 text-pretty">{row.lastRejectReason}</span>
          </p>
        )}
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-border py-1.5 pr-1.5 pl-5">
        <p className="flex min-w-0 items-center gap-1.5 text-xs text-text-muted">
          <CalendarClock size={14} aria-hidden className="shrink-0" />
          <span className="sr-only">{t('writeOff.submittedAt')}: </span>
          <time dateTime={row.submittedAt} className="tabular truncate">
            {when.date} <span className="text-text-faint">{when.time}</span>
          </time>
        </p>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
          aria-label={`${t('writeOff.details')}: ${row.docNo}`}
          className={cn(
            'inline-flex min-h-[var(--size-control)] items-center gap-1 rounded-xl px-3',
            'text-sm font-medium text-accent transition-colors duration-[var(--motion-fast)]',
            'outline-none hover:bg-accent-soft focus-visible:ring-2 focus-visible:ring-accent',
          )}
        >
          {t('writeOff.details')}
          <ChevronRight
            size={16}
            aria-hidden
            className="transition-transform duration-[var(--motion-fast)] motion-safe:group-hover:translate-x-0.5"
          />
        </button>
      </footer>
    </article>
  );
};

const Fact = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-[0.6875rem] font-medium tracking-wide text-text-muted uppercase">{label}</dt>
    <dd className={cn('min-w-0 truncate font-medium text-text', mono && 'tabular font-mono text-[0.8125rem]')}>
      {value}
    </dd>
  </div>
);
